import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { Subscription, debounceTime, finalize } from 'rxjs';
import { BylawsWizardStepComponent } from 'src/app/tools/bylaws/components/bylaws-wizard-step/bylaws-wizard-step.component';
import { BylawsPreviewComponent } from 'src/app/tools/bylaws/components/bylaws-preview/bylaws-preview.component';
import type {
  UISchemaQuestion,
  UISchemaSection,
} from 'projects/bylaws-kit/src/lib/ui-schema';
import {
  BylawsDraft,
  BylawsWizardService,
} from 'src/app/tools/bylaws/services/bylaws-wizard.service';
import { TemplateRendererService } from 'src/app/core/services/template-renderer.service';
import { BackendService } from 'src/app/core/services/backend.service';
import { BylawsRenderService } from 'src/app/services/bylaws-render.service';
import { UISchemaService } from '../../ui-schema.service';
import { uiToRegistryAnswers } from '../../form-normalizer';
import {
  SendBylawsRequest,
  SCHEMA_VERSION,
  TEMPLATE_VERSION,
} from '@bignest/bylaws-kit';

@Component({
  standalone: true,
  selector: 'bylaws-builder',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    BylawsWizardStepComponent,
    BylawsPreviewComponent,
  ],
  templateUrl: './bylaws-wizard.component.html',
  styleUrls: ['./bylaws-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BylawsWizardComponent implements OnInit, OnDestroy {
  sections: UISchemaSection[] = [];
  form: FormGroup | null = null;
  currentDraft: Partial<BylawsDraft> | null = null;
  currentSectionIndex = 0;
  previewMode = false;
  /** Index of the step where we should show per-question errors, or null if none */
  showErrorsForStepIndex: number | null = null;
  private readonly reviewStepLabel = 'Review & Save';
  statusMessage = '';
  errorMessage = '';
  loadingSchema = true;
  email: string | null = null;
  optInToTips = true;
  isEmailSending = false;
  emailSent = false;
  emailError: string | null = null;

  @ViewChild('wizardTop') wizardTop?: ElementRef<HTMLElement>;

  private pendingDraft: Partial<BylawsDraft> | null = null;
  private formSub?: Subscription;
  private draftSub?: Subscription;
  private saveMessageTimeout?: ReturnType<typeof setTimeout>;
  private completedSectionIds = new Set<string>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly wizardService: BylawsWizardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly docxRenderer: TemplateRendererService,
    private readonly bylawsRenderer: BylawsRenderService,
    private readonly backend: BackendService,
    private readonly uiSchema: UISchemaService,
  ) {}

  ngOnInit(): void {
    this.watchDraft();
    void this.fetchSchema();
  }

  private shouldShowQuestion(question: UISchemaQuestion): boolean {
    const dependency = question.visibleIf;
    if (!dependency) {
      return true;
    }

    if (dependency.endsWith('Specific')) {
      const field = dependency.replace(/Specific$/, '');
      const control = this.form?.get(field);
      const value =
        control?.value ??
        (this.currentDraft ? (this.currentDraft as any)[field] : undefined);

      return value === 'Specific number';
    }

    const control = this.form?.get(dependency);
    const value =
      control?.value ??
      (this.currentDraft ? (this.currentDraft as any)[dependency] : undefined);

    return this.isTruthy(value);
  }

  private isTruthy(value: unknown): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === 'false' || normalized === 'no') {
        return false;
      }
      if (normalized === 'true' || normalized === 'yes') {
        return true;
      }
      return Boolean(normalized);
    }
    return Boolean(value);
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.draftSub?.unsubscribe();
    if (this.saveMessageTimeout) {
      clearTimeout(this.saveMessageTimeout);
    }
  }

  get progressLabel(): string {
    if (!this.sections.length) {
      return 'Loading…';
    }
    const stepNumber = this.previewMode
      ? this.sections.length
      : this.currentSectionIndex + 1;
    const sectionLabel = this.previewMode
      ? this.reviewStepLabel
      : (this.sections[this.currentSectionIndex]?.sidebarLabel ??
        this.sections[this.currentSectionIndex]?.title ??
        '');
    return `Step ${stepNumber} · ${sectionLabel}`;
  }

  get forwardLabel(): string {
    if (!this.sections.length) {
      return 'Next';
    }
    return this.currentSectionIndex === this.sections.length - 1
      ? 'Preview'
      : 'Next';
  }

  get isFinalStep(): boolean {
    return this.previewMode;
  }

  prevStep(): void {
    if (!this.sections.length) {
      return;
    }
    if (this.previewMode) {
      this.previewMode = false;
      this.showErrorsForStepIndex = null;
      this.cdr.markForCheck();
      this.scrollToTop();
      return;
    }
    if (this.currentSectionIndex === 0) {
      return;
    }
    this.currentSectionIndex = Math.max(0, this.currentSectionIndex - 1);
    this.showErrorsForStepIndex = null;
    this.cdr.markForCheck();
    this.scrollToTop();
  }

  nextStep(): void {
    if (!this.sections.length) {
      return;
    }
    const currentComplete = this.isSectionComplete(this.currentSectionIndex);
    if (!currentComplete) {
      // NEW: remember which step should show per-question errors
      this.showErrorsForStepIndex = this.currentSectionIndex;
      this.setStatusMessage('Please finish this step before moving on.');
      this.cdr.markForCheck();
      this.scrollToTop();
      return;
    }

    // NEW: mark the current section as completed before moving off it
    this.markSectionCompleted(this.currentSectionIndex);

    const lastIndex = this.sections.length - 1;
    if (this.currentSectionIndex === lastIndex) {
      const firstIncomplete = this.findFirstIncompleteSectionIndex();
      if (firstIncomplete !== null) {
        this.previewMode = false;
        this.currentSectionIndex = firstIncomplete;
        this.showErrorsForStepIndex = null;
        this.setStatusMessage(
          'Please finish all steps before previewing your bylaws.',
        );
        this.cdr.markForCheck();
        this.scrollToTop();
        return;
      }

      this.setPreviewMode(true);
      this.scrollToTop();
      return;
    }

    this.currentSectionIndex = Math.min(
      lastIndex,
      this.currentSectionIndex + 1,
    );
    this.previewMode = false;
    this.showErrorsForStepIndex = null;
    this.cdr.markForCheck();
    this.scrollToTop();
  }

  goToStep(index: number): void {
    if (!this.sections.length) {
      return;
    }
    if (index < 0 || index >= this.sections.length) {
      return;
    }

    if (index <= this.currentSectionIndex) {
      this.previewMode = false;
      this.currentSectionIndex = index;
      this.showErrorsForStepIndex = null;
      this.cdr.markForCheck();
      this.scrollToTop();
      return;
    }

    const furthest = this.getFurthestAccessibleIndex();

    if (this.allSectionsComplete()) {
      this.previewMode = false;
      this.currentSectionIndex = index;
      this.showErrorsForStepIndex = null;
      this.cdr.markForCheck();
      this.scrollToTop();
      return;
    }

    if (index > furthest) {
      this.previewMode = false;
      this.currentSectionIndex = furthest;
      this.showErrorsForStepIndex = null;
      this.setStatusMessage(
        'Please complete each step in order before jumping ahead.',
      );
      this.cdr.markForCheck();
      this.scrollToTop();
      return;
    }

    this.previewMode = false;
    this.currentSectionIndex = index;
    this.showErrorsForStepIndex = null;
    this.cdr.markForCheck();
    this.scrollToTop();
  }

  setPreviewMode(state: boolean): void {
    if (state) {
      const firstIncomplete = this.findFirstIncompleteSectionIndex();
      if (firstIncomplete !== null) {
        this.previewMode = false;
        this.currentSectionIndex = firstIncomplete;
        this.showErrorsForStepIndex = null;
        this.setStatusMessage(
          'Please finish all steps before previewing your bylaws.',
        );
        this.cdr.markForCheck();
        this.scrollToTop();
        return;
      }
    }

    this.previewMode = state;
    this.showErrorsForStepIndex = null;
    this.cdr.markForCheck();
    this.scrollToTop();
  }

  manualSave(): void {
    if (!this.form) {
      return;
    }
    const snapshot = this.normalizeDraftValues(this.form.getRawValue());
    this.wizardService.update(snapshot);
    this.currentDraft = snapshot;
    this.setStatusMessage('Saved just now');
  }

  startOver(): void {
    const defaults = this.buildDefaultValues(true);
    if (this.form) {
      this.form.reset(defaults, { emitEvent: false });
    } else {
      const controls: Record<string, any> = {};
      Object.entries(defaults).forEach(
        ([key, value]) => (controls[key] = [value]),
      );
      this.form = this.fb.group(controls);
      this.subscribeToFormChanges();
    }
    const normalizedDefaults = this.normalizeDraftValues(defaults);
    this.wizardService.reset();
    this.wizardService.update(normalizedDefaults);
    this.pendingDraft = normalizedDefaults;
    this.currentDraft = normalizedDefaults;
    this.currentSectionIndex = 0;
    this.completedSectionIds.clear();
    this.setPreviewMode(false);
    this.setStatusMessage('Started a fresh draft');
    this.cdr.markForCheck();
  }

  confirmStartOver(): void {
    if (
      !window.confirm(
        'Starting over will clear your current answers. Do you want to continue?',
      )
    ) {
      return;
    }

    this.startOver();
  }

  onEditAnswers(): void {
    this.goToStep(0);
  }

  trackSection(_: number, section: UISchemaSection): string {
    return section.id;
  }

  private markSectionCompleted(index: number): void {
    const section = this.sections[index];
    if (!section?.id) {
      return;
    }

    if (this.isSectionComplete(index)) {
      this.completedSectionIds.add(section.id);
    } else {
      this.completedSectionIds.delete(section.id);
    }
  }

  hasCompletedSection(index: number): boolean {
    const section = this.sections[index];
    if (!section?.id) {
      return false;
    }
    return this.completedSectionIds.has(section.id);
  }

  private seedCompletedSectionsFromDraft(): void {
    this.completedSectionIds.clear();

    if (!this.sections.length || !this.currentDraft) {
      return;
    }

    this.sections.forEach((section, index) => {
      if (section.id && this.isSectionComplete(index)) {
        this.completedSectionIds.add(section.id);
      }
    });
  }

  private async fetchSchema(): Promise<void> {
    try {
      const { sections } = await this.uiSchema.load();
      this.sections = sections;
      this.errorMessage = '';
      this.loadingSchema = false;
      this.buildForm();
    } catch (error) {
      console.error('Failed to load the UI schema', error);
      this.errorMessage =
        'Unable to load the bylaws schema. Please refresh and try again.';
      this.loadingSchema = false;
    } finally {
      this.cdr.markForCheck();
    }
  }

  private watchDraft(): void {
    this.draftSub = this.wizardService.draft$.subscribe((draft) => {
      this.pendingDraft = draft;
      if (!this.form) {
        return;
      }
      if (draft) {
        this.patchFormFromDraft(draft);

        this.currentDraft = draft;
      } else {
        const defaults = this.buildDefaultValues(true);
        this.form.reset(defaults, { emitEvent: false });
        this.currentDraft = this.normalizeDraftValues(defaults);
      }
      this.cdr.markForCheck();
    });
  }

  private buildForm(): void {
    if (!this.sections.length) {
      return;
    }
    const defaults = this.buildDefaultValues();
    const controls: Record<string, any> = {};
    this.sections.forEach((section) => {
      section.questions.forEach((question) => {
        const key = question.key;
        controls[key] = [defaults[key]];
      });
    });
    this.form = this.fb.group(controls);
    this.currentDraft = this.normalizeDraftValues(defaults);
    if (this.pendingDraft) {
      this.patchFormFromDraft(this.pendingDraft);
      this.currentDraft = this.pendingDraft;
    }
    this.subscribeToFormChanges();
    this.completedSectionIds.clear();
    if (this.pendingDraft) {
      this.seedCompletedSectionsFromDraft();
    }
  }

  private subscribeToFormChanges(): void {
    if (!this.form) {
      return;
    }
    this.formSub?.unsubscribe();
    this.formSub = this.form.valueChanges
      .pipe(debounceTime(250))
      .subscribe((value) => {
        const normalized = this.normalizeDraftValues(value);
        this.wizardService.update(normalized);
        this.currentDraft = normalized;
        this.setStatusMessage('Saved just now');

        if (this.sections.length) {
          this.markSectionCompleted(this.currentSectionIndex);
        }
      });
  }

  private buildDefaultValues(ignoreSaved = false): Record<string, any> {
    if (!this.sections.length) {
      return {};
    }

    const source =
      !ignoreSaved && this.pendingDraft
        ? (this.pendingDraft as Record<string, any>)
        : null;

    const values: Record<string, any> = {};

    this.sections.forEach((section) => {
      section.questions.forEach((question) => {
        const key = question.key;
        if (source && Object.prototype.hasOwnProperty.call(source, key)) {
          values[key] = source[key];
          return;
        }
        if (question.default !== undefined) {
          values[key] = question.default;
          return;
        }
        switch (question.type) {
          case 'boolean':
            values[key] = false;
            break;
          case 'number':
            values[key] = null;
            break;
          case 'select':
            values[key] = question.prompt
              ? ''
              : (question.options?.[0] ?? null);
            break;
          default:
            values[key] = '';
        }
      });
    });

    if (values['duesRequired'] === undefined) {
      values['duesRequired'] = 'No';
    }
    if (typeof values['duesRequired'] === 'boolean') {
      values['duesRequired'] = values['duesRequired'] ? 'Yes' : 'No';
    }

    values['officers'] = this.buildOfficersList(values as Partial<BylawsDraft>);

    if (
      values['quorumExecBoard'] === undefined ||
      values['quorumExecBoard'] === null ||
      values['quorumExecBoard'] === ''
    ) {
      values['quorumExecBoard'] = 'half the number of board members plus one';
    }

    return values;
  }

  private setStatusMessage(message: string): void {
    this.statusMessage = message;
    if (this.saveMessageTimeout) {
      clearTimeout(this.saveMessageTimeout);
    }
    this.saveMessageTimeout = setTimeout(() => {
      this.statusMessage = '';
      this.cdr.markForCheck();
    }, 4000);
    this.cdr.markForCheck();
  }

  private normalizeDraftValues(
    value: Record<string, any>,
  ): Partial<BylawsDraft> {
    const normalized: Record<string, any> = uiToRegistryAnswers({ ...value });
    const numericKeys: Array<keyof BylawsDraft> = [
      'termLength',
      'termLimit',
      'quorumExecNumber',
      'noticeDays',
      'fiscalYearStartDay',
      'fiscalYearEndDay',
      'routineSpendingLimit',
      'nonBudgetedBoardLimit',
      'amendmentNoticeDays',
      'duesAmount',
    ];

    numericKeys.forEach((key) => {
      const current = normalized[key as string];
      if (current === '' || current === undefined) {
        normalized[key as string] = null;
        return;
      }
      if (current === null || typeof current === 'number') {
        return;
      }
      const parsed = Number(current);
      normalized[key as string] = Number.isNaN(parsed) ? current : parsed;
    });

    normalized['officers'] = this.buildOfficersList(
      normalized as Partial<BylawsDraft>,
    );

    const execMode = normalized['quorumExecMode'];
    if (execMode === 'Specific number') {
      normalized['quorumExecBoard'] = normalized['quorumExecNumber'];
    } else {
      normalized['quorumExecBoard'] =
        execMode || 'half the number of board members plus one';
    }

    return normalized as Partial<BylawsDraft>;
  }

  private buildOfficersList(draft: Partial<BylawsDraft>): string {
    const entries: Array<[keyof BylawsDraft, string]> = [
      ['officerPresident', 'President'],
      ['officerVicePresident', 'Vice-President'],
      ['officerSecretary', 'Secretary'],
      ['officerTreasurer', 'Treasurer'],
      ['officerViceTreasurer', 'Vice-Treasurer'],
      ['officerMemberAtLarge', 'Member-at-Large'],
      ['officerCommunications', 'Communications Coordinator'],
      ['officerVolunteerCoordinator', 'Volunteer Coordinator'],
    ];

    return entries
      .filter(([key]) => Boolean(draft[key]))
      .map(([, label]) => label)
      .join(', ');
  }

  private patchFormFromDraft(draft: Partial<BylawsDraft>): void {
    if (!this.form) {
      return;
    }
    this.form.patchValue(this.mapDraftForForm(draft), { emitEvent: false });
  }

  private mapDraftForForm(draft: Partial<BylawsDraft>): Record<string, any> {
    const formValue: Record<string, any> = { ...draft };
    if (draft.duesRequired !== undefined) {
      formValue['duesRequired'] = draft.duesRequired ? 'Yes' : 'No';
    }
    return formValue;
  }

  isSectionComplete(index: number): boolean {
    if (!this.currentDraft) {
      return false;
    }
    const section = this.sections[index];
    if (!section) {
      return false;
    }

    return section.questions.every((question) => {
      if (!this.shouldShowQuestion(question)) {
        return true;
      }
      const value = (this.currentDraft as any)[question.key];

      if (question.type === 'boolean') {
        return value === true || value === false;
      }

      if (question.type === 'number') {
        return value !== null && value !== undefined && value !== '';
      }

      if (value === null || value === undefined) {
        return false;
      }

      return String(value).trim().length > 0;
    });
  }

  private allSectionsComplete(): boolean {
    if (!this.sections.length || !this.currentDraft) {
      return false;
    }
    return this.sections.every((_, idx) => this.isSectionComplete(idx));
  }

  private findFirstIncompleteSectionIndex(): number | null {
    if (!this.sections.length || !this.currentDraft) {
      return null;
    }
    for (let i = 0; i < this.sections.length; i++) {
      if (!this.isSectionComplete(i)) {
        return i;
      }
    }
    return null;
  }

  private getFurthestAccessibleIndex(): number {
    if (!this.sections.length || !this.currentDraft) {
      return 0;
    }
    if (this.allSectionsComplete()) {
      return this.sections.length - 1;
    }
    let furthest = 0;
    for (let i = 0; i < this.sections.length; i++) {
      if (this.isSectionComplete(i)) {
        furthest = i;
        continue;
      }
      break;
    }
    return furthest;
  }

  async downloadDocx(): Promise<void> {
    if (!this.currentDraft) {
      return;
    }

    if (!this.allSectionsComplete()) {
      const firstIncomplete = this.findFirstIncompleteSectionIndex();
      if (firstIncomplete !== null) {
        this.previewMode = false;
        this.currentSectionIndex = firstIncomplete;
        this.showErrorsForStepIndex = null;
        this.setStatusMessage(
          'Please finish all steps before downloading your bylaws.',
        );
        this.cdr.markForCheck();
        this.scrollToTop();
      }
      return;
    }

    try {
      const { markdown, errors } = await this.bylawsRenderer.render(
        this.currentDraft as Record<string, unknown>,
      );

      if (errors.length) {
        this.setStatusMessage('Resolve preview errors before downloading.');
        return;
      }

      if (!markdown) {
        return;
      }

      const blob = await this.docxRenderer.buildDocxFromMarkdown(markdown);
      const filename = `${this.currentDraft.orgName} bylaws.docx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.setStatusMessage('Downloaded .docx file');
    } catch (err) {
      console.error('Failed to download .docx', err);
      this.setStatusMessage('Unable to download right now');
    }
  }

  onDownloadDocx(): void {
    void this.downloadDocx();
  }

  onEmailBylaws(): void {
    const emailValue = (this.email || '').trim();
    if (!emailValue) {
      this.emailError = 'Please enter your email address.';
      return;
    }

    if (!this.isEmailValid(emailValue)) {
      this.emailError = 'Please enter a valid email address.';
      return;
    }

    const payload = this.createSendBylawsPayload(emailValue);
    if (!payload) {
      this.emailError = 'Complete your bylaws before emailing a copy.';
      return;
    }

    this.emailError = null;
    this.emailSent = false;
    this.isEmailSending = true;

    this.backend
      .sendBylaws(payload)
      .pipe(
        finalize(() => {
          this.isEmailSending = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.emailSent = true;
        },
        error: (err) => {
          console.error('sendBylaws failed', err);
          this.emailError =
            "Sorry, we couldn't email your bylaws. Please try again.";
        },
      });
  }

  private createSendBylawsPayload(email: string): SendBylawsRequest | null {
    const draft = this.wizardService.currentDraftValue();
    if (!draft) {
      return null;
    }

    return {
      email,
      marketingConsent: !!this.optInToTips,
      schemaVersion: SCHEMA_VERSION,
      templateVersion: TEMPLATE_VERSION,
      answers: draft as any,
    };
  }

  private isEmailValid(email: string): boolean {
    return /\S+@\S+\.\S+/.test(email);
  }

  private scrollToTop(): void {
    // Defensively guard in case ViewChild isn't ready yet
    const el = this.wizardTop?.nativeElement;
    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
